import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Socket } from 'ngx-socket-io';
import { environment } from 'src/environments/environment';
import { AuthService } from '../login/auth.service';
import { UserService } from '../services/user.service';
import {
  CreateMarkupDto,
  ApplyClientMarkupTemplateDto,
  ClientMarkup,
  MarkUpType,
  Markup,
  ResolveMarkupPriceDto,
  ResolveMarkupPriceResponse,
  SearchMarkupDto,
  UpdateMarkupDto
} from './model/markup.model';
import { firstValueFrom } from 'rxjs';
import { Product } from 'src/typings';

@Injectable({
  providedIn: 'root'
})
export class MarkupService implements OnDestroy {
  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private userService: UserService,
    private socket: Socket
  ) {
    this.loadMarkups();
  }

  ngOnInit(): void {
    this.loadMarkups();
  }

  ngOnDestroy(): void {
    this.createEventSubscription.unsubscribe();
    this.updateEventSubscription.unsubscribe();
    this.deleteEventSubscription.unsubscribe();
    this.clientCreateEventSubscription.unsubscribe();
    this.clientUpdateEventSubscription.unsubscribe();
    this.clientDeleteEventSubscription.unsubscribe();
    this.clientTemplateAppliedEventSubscription.unsubscribe();
  }

  allMarkups: Markup[] = [];

  clientMarkupsByClientId = new Map<number, ClientMarkup[]>();

  createEvent = this.socket.fromEvent<Markup>('newMarkup');

  createEventSubscription = this.createEvent.subscribe(() => {
    this.loadMarkups();
  });

  updatedEvent = this.socket.fromEvent<Markup>('updatedMarkUp');

  updateEventSubscription = this.updatedEvent.subscribe(() => {
    this.loadMarkups();
  });

  deleteEvent = this.socket.fromEvent<Markup>('deteleMarkUp');

  deleteEventSubscription = this.deleteEvent.subscribe(() => {
    this.loadMarkups();
  });

  clientCreateEvent = this.socket.fromEvent<{ clientId: number; markup: ClientMarkup }>(
    'newClientMarkup'
  );

  clientCreateEventSubscription = this.clientCreateEvent.subscribe((event) => {
    if (event?.clientId) this.loadClientMarkups(event.clientId);
  });

  clientUpdatedEvent = this.socket.fromEvent<{ clientId: number; markup: ClientMarkup }>(
    'updatedClientMarkup'
  );

  clientUpdateEventSubscription = this.clientUpdatedEvent.subscribe((event) => {
    if (event?.clientId) this.loadClientMarkups(event.clientId);
  });

  clientDeleteEvent = this.socket.fromEvent<{ clientId: number; markup: ClientMarkup }>(
    'deleteClientMarkup'
  );

  clientDeleteEventSubscription = this.clientDeleteEvent.subscribe((event) => {
    if (event?.clientId) this.loadClientMarkups(event.clientId);
  });

  clientTemplateAppliedEvent = this.socket.fromEvent<{
    clientId: number;
    deltaPercent?: number;
    template?: string;
    count: number;
  }>('clientMarkupTemplateApplied');

  clientTemplateAppliedEventSubscription = this.clientTemplateAppliedEvent.subscribe((event) => {
    if (event?.clientId) this.loadClientMarkups(event.clientId);
  });

  async loadMarkups() {
    this.allMarkups = await firstValueFrom(this.search({ order: 'DESC', orderBy: 'priority' }));
  }

  async loadClientMarkups(clientId: number) {
    const normalizedClientId = Number(clientId || 0);
    if (!normalizedClientId) return [];
    const markups = await firstValueFrom(
      this.searchClient(normalizedClientId, { order: 'DESC', orderBy: 'priority' })
    );
    this.clientMarkupsByClientId.set(normalizedClientId, markups);
    return markups;
  }

  search(payload?: SearchMarkupDto) {
    return this.http.post<Markup[]>(`${environment.BACKEND}/markup/search`, payload);
  }

  create(payload: CreateMarkupDto) {
    return this.http.post<Markup>(`${environment.BACKEND}/markup/create`, payload);
  }

  update(payload: UpdateMarkupDto) {
    return this.http.put<Markup>(`${environment.BACKEND}/markup/update`, payload);
  }

  updatePrice() {
    return this.http.post(`${environment.BACKEND}/markup/price_update`, null);
  }

  delete(id: number) {
    return this.http.delete(`${environment.BACKEND}/markup/delete/${id}`);
  }

  searchClient(clientId: number, payload?: SearchMarkupDto) {
    return this.http.post<ClientMarkup[]>(
      `${environment.BACKEND}/markup/client/${clientId}/search`,
      payload
    );
  }

  createClient(clientId: number, payload: CreateMarkupDto) {
    return this.http.post<ClientMarkup>(
      `${environment.BACKEND}/markup/client/${clientId}/create`,
      payload
    );
  }

  applyClientTemplate(clientId: number, payload: ApplyClientMarkupTemplateDto) {
    return this.http.post<ClientMarkup[]>(
      `${environment.BACKEND}/markup/client/${clientId}/apply-template`,
      payload
    );
  }

  applyClientHistoryTemplate(clientId: number) {
    return this.http.post<ClientMarkup[]>(
      `${environment.BACKEND}/markup/client/${clientId}/apply-history-template`,
      null
    );
  }

  updateClient(clientId: number, payload: UpdateMarkupDto) {
    return this.http.put<ClientMarkup>(
      `${environment.BACKEND}/markup/client/${clientId}/update`,
      payload
    );
  }

  deleteClient(clientId: number, id: number) {
    return this.http.delete(`${environment.BACKEND}/markup/client/${clientId}/delete/${id}`);
  }

  resolvePrice(payload: ResolveMarkupPriceDto) {
    return this.http.post<ResolveMarkupPriceResponse>(
      `${environment.BACKEND}/markup/resolve-price`,
      payload
    );
  }

  getMarkup(product: Product, price: number, rules: Markup[] = this.allMarkups) {
    let matchedMarkups: Markup[] = [];

    for (let index = 0; index < rules.length; index++) {
      const markup = rules[index];
      if (markup.rangeStart > price) continue;
      if (markup.rangeEnd !== null && markup.rangeEnd !== undefined && markup.rangeEnd < price)
        continue;
      if (markup.folder) {
        if (!product.folders) continue;
        const find = product.folders.find((f) => f.id === markup.folder?.id);
        if (!find) continue;
      }
      if ((markup.brands || []).length !== 0) {
        const find = (markup.brands || []).find((b) => b.id === product.brand?.id);
        if (!find) continue;
      }
      matchedMarkups.push(markup);
    }

    const mainMarkup = matchedMarkups.reduce<Markup>(
      (prev, current) => {
        if (!prev.id) return current;
        if (prev.priority !== current.priority) return prev.priority > current.priority ? prev : current;
        const prevIsTemplate = prev.templateGenerated === true;
        const currentIsTemplate = current.templateGenerated === true;
        if (prevIsTemplate !== currentIsTemplate) {
          return currentIsTemplate ? prev : current;
        }
        return prev.id > current.id ? prev : current;
      },
      {
        id: 0,
        rangeEnd: null,
        rangeStart: 0,
        markup: 30,
        priority: 1,
        type: MarkUpType.PERCENT,
        round: 10
      }
    );

    return mainMarkup;
  }

  getRetailPrice(product: Product, price: number) {
    return this.getRetailPriceByRules(product, price, this.allMarkups);
  }

  getClientRetailPrice(product: Product, price: number, clientId?: number | null) {
    const normalizedClientId = Number(clientId || 0);
    const clientRules = normalizedClientId
      ? this.clientMarkupsByClientId.get(normalizedClientId) || []
      : [];
    const clientMarkup = this.getMatchedMarkup(product, price, clientRules);
    if (clientMarkup) {
      return this.calculateRetailByMarkup(clientMarkup, price);
    }
    return this.getRetailPriceByRules(product, price, this.allMarkups);
  }

  async getClientRetailPriceAsync(product: Product, price: number, clientId?: number | null) {
    const normalizedClientId = Number(clientId || 0);
    if (normalizedClientId && !this.clientMarkupsByClientId.has(normalizedClientId)) {
      await this.loadClientMarkups(normalizedClientId);
    }
    return this.getClientRetailPrice(product, price, normalizedClientId);
  }

  private getRetailPriceByRules(product: Product, price: number, rules: Markup[]) {
    price = Number(price);

    const mainMarkup = this.getMarkup(product, price, rules);
    return this.calculateRetailByMarkup(mainMarkup, price);
  }

  private calculateRetailByMarkup(mainMarkup: Markup, price: number) {
    const round = Number(mainMarkup.round || 1);
    if (round <= 0) return 0;

    if (mainMarkup.type == MarkUpType.FIXED) {
      return (
        Math.round((Number(price) + Number(mainMarkup.markup)) / round) *
        round
      );
    }

    if (mainMarkup.type == MarkUpType.PERCENT) {
      return (
        Math.round((Number(price) * Number(mainMarkup.markup / 100 + 1)) / round) *
        round
      );
    }
    return 0;
  }

  private getMatchedMarkup(product: Product, price: number, rules: Markup[]) {
    const matched = this.getMarkup(product, price, rules);
    return matched.id ? matched : null;
  }
}
